import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validators,
  AbstractControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';

@Component({
  selector: 'app-budget-allocation',
  templateUrl: './budget-allocation.component.html',
  styleUrls: ['./budget-allocation.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BudgetAllocationComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BudgetAllocationComponent),
      multi: true,
    },
  ],
})
export class BudgetAllocationComponent implements OnInit, ControlValueAccessor, Validator {
  @Input() plBalance = 0; // Parent's available pay later cap balance
  @Input() acBalance = 0; // Parent's available deposit balance
  @Input() isEdit = false;
  @Input() subaccountPaylaterBalance = 0; // Subaccount's current pay later cap balance
  @Input() subaccountDepositBalance = 0; // Subaccount's current deposit balance
  @Input() subaccountTotalBudget = 0; // Subaccount's total allocated budget
  @Input() subaccountTotalDepositBudget = 0; // Subaccount's total allocated deposit budget
  @Input() subaccountTotalPaylaterCap = 0; // Subaccount's total allocated pay later cap

  budgetForm!: FormGroup;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidationChange: () => void = () => {};

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initializeForm();
    this.setupValueChanges();
  }

  private initializeForm(): void {
    this.budgetForm = this.fb.group({
      depositBudget: [
        null,
        [
          Validators.pattern(/^\d+(\.\d{1,2})?$/),
        ],
      ],
      totalBudget: [
        null,
        [
          Validators.pattern(/^\d+(\.\d{1,2})?$/),
        ],
      ],
      payLaterBudget: [{ value: 0, disabled: true }],
    });
  }

  private setupValueChanges(): void {
    const updateForm = () => {
      this.calculatePayLaterCap();
      this.validateBudgets();
      this.emitValue();
    };

    this.budgetForm.get('depositBudget')?.valueChanges.subscribe(updateForm);
    this.budgetForm.get('totalBudget')?.valueChanges.subscribe(updateForm);
  }

  private calculatePayLaterCap(): void {
    // Pay Later Cap is calculated as totalBudget - depositBudget
    const total = this.getNumberValue('totalBudget');
    const deposit = this.getNumberValue('depositBudget');
    const payLaterCap = total - deposit;
    this.budgetForm
      .get('payLaterBudget')
      ?.setValue(this.formatToTwoDecimals(payLaterCap), { emitEvent: false });
  }

  private validateBudgets(): void {
    const depositBudget = this.getNumberValue('depositBudget');
    const totalBudget = this.getNumberValue('totalBudget');
    const payLaterCap = this.roundToTwoDecimals(totalBudget - depositBudget);
    const depositBudgetControl = this.budgetForm.get('depositBudget');
    const totalBudgetControl = this.budgetForm.get('totalBudget');

    // Validate deposit_budget does not exceed available deposit balance
    const maxDepositAvailable = this.roundToTwoDecimals(
      this.isEdit 
        ? this.subaccountDepositBalance + this.acBalance 
        : this.acBalance
    );
    if (this.roundToTwoDecimals(depositBudget) > maxDepositAvailable) {
      this.setControlError(depositBudgetControl!, 'exceededAcBalance', true);
    } else {
      this.removeControlError(depositBudgetControl!, 'exceededAcBalance');
    }

    // Validate depositBudget does not exceed totalBudget
    if (this.roundToTwoDecimals(depositBudget) > this.roundToTwoDecimals(totalBudget)) {
      this.setControlError(depositBudgetControl!, 'exceededTotalBudget', true);
    } else {
      this.removeControlError(depositBudgetControl!, 'exceededTotalBudget');
    }

    // Validate totalBudget does not exceed max total budget
    const maxTotalAvailable = this.roundToTwoDecimals(this.getMaxTotalBudget());
    if (this.roundToTwoDecimals(totalBudget) > maxTotalAvailable) {
      this.setControlError(totalBudgetControl!, 'exceededMaxTotal', true);
      this.removeControlError(totalBudgetControl!, 'exceededPlBalance');
    } else {
      this.removeControlError(totalBudgetControl!, 'exceededMaxTotal');
      
      // Only validate payLaterCap if totalBudget is within allowed range
      const maxPayLaterAvailable = this.roundToTwoDecimals(
        this.isEdit 
          ? this.subaccountPaylaterBalance + this.plBalance 
          : this.plBalance
      );
      if (payLaterCap > maxPayLaterAvailable) {
        this.setControlError(totalBudgetControl!, 'exceededPlBalance', true);
      } else {
        this.removeControlError(totalBudgetControl!, 'exceededPlBalance');
      }
    }

    // Auto-mark controls as touched when invalid to show errors immediately
    if (depositBudgetControl?.invalid) {
      depositBudgetControl.markAsTouched();
    }
    if (totalBudgetControl?.invalid) {
      totalBudgetControl.markAsTouched();
    }

    // Notify parent form about validation state change
    this.onValidationChange();
  }

  private setControlError(
    control: AbstractControl,
    errorKey: string,
    value: boolean
  ): void {
    const errors = { ...control.errors, [errorKey]: value };
    control.setErrors(errors);
  }

  private removeControlError(control: AbstractControl, errorKey: string): void {
    if (!control.hasError(errorKey)) return;

    const errors = { ...control.errors };
    delete errors[errorKey];
    control.setErrors(Object.keys(errors).length > 0 ? errors : null);
  }

  private roundToTwoDecimals(value: any): number {
    const numeric = Number(value);
    const safe = Number.isFinite(numeric) ? numeric : 0;
    return Math.round(safe * 100) / 100;
  }

  private formatToTwoDecimals(value: any): string {
    return this.roundToTwoDecimals(value).toFixed(2);
  }

  private markAllAsTouched(): void {
    Object.keys(this.budgetForm.controls).forEach((key) => {
      this.budgetForm.get(key)?.markAsTouched();
    });
  }

  getNumberValue(controlName: string): number {
    return this.roundToTwoDecimals(this.budgetForm.get(controlName)?.value);
  }

  numberOnly(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  getMaxTotalBudget(): number {
    if (this.isEdit) {
      return this.plBalance + this.acBalance + this.subaccountPaylaterBalance + this.subaccountDepositBalance;
    }
    return this.plBalance + this.acBalance;
  }

  getCurrentTotalBudget(): number {
    return this.subaccountPaylaterBalance + this.subaccountDepositBalance;
  }

  getTotalBudgetCurrent(): number {
    if (this.isEdit) {
      return this.getCurrentTotalBudget();
    }
    return 0;
  }

  getDepositBudgetCurrent(): number {
    if (this.isEdit) {
      return this.subaccountDepositBalance;
    }
    return 0;
  }

  getMaxDepositBudget(): number {
    const maxDepositAvailable = this.isEdit 
      ? this.subaccountDepositBalance + this.acBalance 
      : this.acBalance;
    return this.roundToTwoDecimals(maxDepositAvailable);
  }

  getPayLaterCapCurrent(): number {
    if (this.isEdit) {
      return this.subaccountPaylaterBalance;
    }
    return 0;
  }

  getMaxPayLaterCap(): number {
    const maxPayLaterAvailable = this.isEdit 
      ? this.subaccountPaylaterBalance + this.plBalance 
      : this.plBalance;
    return this.roundToTwoDecimals(maxPayLaterAvailable);
  }

  getDepositDelta(): number {
    if (!this.isEdit) return 0;
    const newValue = this.getNumberValue('depositBudget');
    return this.roundToTwoDecimals(newValue - this.subaccountDepositBalance);
  }

  getPayLaterDelta(): number {
    if (!this.isEdit) return 0;
    const newValue = this.getNumberValue('payLaterBudget');
    return this.roundToTwoDecimals(newValue - this.subaccountPaylaterBalance);
  }

  getTotalBudgetDelta(): number {
    if (!this.isEdit) return 0;
    const newValue = this.getNumberValue('totalBudget');
    const currentTotal = this.getCurrentTotalBudget();
    return this.roundToTwoDecimals(newValue - currentTotal);
  }

  formatDelta(delta: number): string {
    if (delta === 0) return '±0';
    return `${Math.abs(delta)}`;
  }

  getNewTotalAllocation(): number {
    if (!this.isEdit) return 0;
    return this.subaccountTotalBudget + this.getTotalBudgetDelta();
  }

  getNewDepositAllocation(): number {
    if (!this.isEdit) return 0;
    return this.subaccountTotalDepositBudget + this.getDepositDelta();
  }

  getNewPayLaterAllocation(): number {
    if (!this.isEdit) return 0;
    return this.subaccountTotalPaylaterCap + this.getPayLaterDelta();
  }

  get f() {
    return this.budgetForm.controls;
  }

  private emitValue(): void {
    const value = {
      depositBudget: this.getNumberValue('depositBudget'),
      totalBudget: this.getNumberValue('totalBudget'),
      payLaterBudget: this.getNumberValue('payLaterBudget'),
    };
    this.onChange(value);
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (!this.budgetForm) {
      return;
    }

    const hasBudgetValues =
      value &&
      (value.totalBudget !== undefined || value.depositBudget !== undefined);

    if (hasBudgetValues) {
      const depositBudgetFormatted = this.formatToTwoDecimals(value.depositBudget);
      const totalBudgetFormatted = this.formatToTwoDecimals(value.totalBudget);

      this.budgetForm.patchValue(
        {
          depositBudget: depositBudgetFormatted,
          totalBudget: totalBudgetFormatted,
        },
        { emitEvent: false }
      );

      // Calculate payLaterBudget as totalBudget - depositBudget
      const payLaterBudget =
        this.roundToTwoDecimals(totalBudgetFormatted) -
        this.roundToTwoDecimals(depositBudgetFormatted);
      this.budgetForm
        .get('payLaterBudget')
        ?.setValue(this.formatToTwoDecimals(payLaterBudget), { emitEvent: false });
      
      // Mark all controls as touched in edit mode to show validation errors immediately
      if (this.isEdit) {
        this.markAllAsTouched();
      }
    } else {
      this.budgetForm.patchValue(
        {
          depositBudget: null,
          totalBudget: null,
        },
        { emitEvent: false }
      );
      this.budgetForm
        .get('payLaterBudget')
        ?.setValue(this.formatToTwoDecimals(0), { emitEvent: false });
    }

    this.calculatePayLaterCap();
    this.validateBudgets();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.budgetForm.disable();
    } else {
      this.budgetForm.enable();
      this.budgetForm.get('payLaterBudget')?.disable();
    }
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.budgetForm) {
      return null;
    }

    const depositBudgetControl = this.budgetForm.get('depositBudget');
    const totalBudgetControl = this.budgetForm.get('totalBudget');

    const errors: ValidationErrors = {};

    if (depositBudgetControl?.hasError('exceededAcBalance')) {
      errors['exceededAcBalance'] = true;
    }

    if (depositBudgetControl?.hasError('exceededTotalBudget')) {
      errors['exceededTotalBudget'] = true;
    }

    if (totalBudgetControl?.hasError('exceededMaxTotal')) {
      errors['exceededMaxTotal'] = true;
    }

    if (totalBudgetControl?.hasError('exceededPlBalance')) {
      errors['exceededPlBalance'] = true;
    }

    if (depositBudgetControl?.hasError('pattern')) {
      errors['depositBudgetPattern'] = true;
    }

    if (totalBudgetControl?.hasError('pattern')) {
      errors['totalBudgetPattern'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidationChange = fn;
  }
}
